import { SectionHead, Reveal, Crosshair } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { Home, Anchor, Banknote, Sun } from "lucide-react";

const ICONS = [Home, Anchor, Banknote, Sun];

export const MicroGrid = () => {
  const { t } = useLang();
  const m = t.micro;
  return (
    <section id="micro" className="relative bg-canvas bp-grid py-24 sm:py-32 border-b border-technical" data-testid="micro-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={m.eyebrow} title={m.title} desc={m.desc} testid="micro" />
        {m.deck && (
          <Reveal delay={0.1}>
            <a href="/assets/Powering_African_Micro-Economies.pdf" target="_blank" rel="noopener noreferrer" data-testid="micro-deck" className="group mt-8 flex flex-col md:flex-row md:items-center gap-5 border border-technical bg-white p-5 sm:p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(122,22,32,0.45)]">
              <span className="chamfer-sm flex h-14 w-14 shrink-0 items-center justify-center bg-ink font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]">PDF</span>
              <span className="min-w-0 flex-1">
                <span className="block font-mono-tech text-[0.65rem] tracking-[0.28em] uppercase text-red-brand">{m.deck.eyebrow}</span>
                <span className="mt-1 block font-display text-lg sm:text-xl font-bold tracking-tight text-ink group-hover:text-red-brand transition-colors">{m.deck.title}</span>
                <span className="mt-1.5 block text-sm leading-relaxed text-[#525964]">{m.deck.desc}</span>
              </span>
              <span className="chamfer-sm inline-flex shrink-0 items-center gap-2 bg-red-brand px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4]">{m.deck.cta} ↗</span>
            </a>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {m.proof.map((p, i) => (
              <span
                key={p}
                data-testid={`micro-proof-${i}`}
                className="chamfer-sm bg-red-brand px-3.5 py-2 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4]"
              >
                {p}
              </span>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-[rgba(22,24,27,0.12)] border border-technical">
          {m.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05} className="bg-white p-5 sm:p-6">
              <div className="font-display text-xl sm:text-2xl font-black tracking-tight text-ink" data-testid={`micro-stat-${i}`}>
                {s.val}
              </div>
              <div className="mt-1.5 font-mono-tech text-[9px] uppercase tracking-[0.13em] text-[#525964]">{s.label}</div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(22,24,27,0.12)] border border-technical">
          {m.cards.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={c.title} delay={i * 0.08} className="relative bg-canvas p-8 sm:p-10 group transition-colors duration-300 hover:bg-white">
                <Crosshair className="top-3 right-3 opacity-40 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex items-center gap-4">
                  <div className="chamfer-sm flex h-11 w-11 shrink-0 items-center justify-center border border-[rgba(122,22,32,0.35)] bg-white">
                    <Icon className="h-5 w-5 text-red-brand" />
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-ink" data-testid={`micro-card-${i}`}>
                    {c.title}
                  </h3>
                </div>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#525964]">{c.desc}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex items-start gap-3 border border-dashed border-[rgba(122,22,32,0.4)] bg-white px-5 py-4" data-testid="micro-note">
            <span className="pulse-dot mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-red-brand" />
            <p className="text-xs sm:text-sm leading-relaxed text-[#525964]">{m.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
